import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
} from '@mui/material';
import { useAppBar } from '../../providers/AppBarProvider';
import { fetchPurchase, formatCurrency, handleError, getDescription, getSubtotal } from '../../utils/lib';
import { useLocation, useNavigate } from 'react-router-dom';
import ViewPage from './ViewPage';
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useConfig } from '../../providers/ConfigProvider';
import BottomSheet from '../../layouts/BottomSheet';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ListItemProductRemove from '../../layouts/ListItemProductRemove';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useTranslation } from 'react-i18next';

function ViewPurchase() {
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [id, setId] = useState(location.state.id);
  const [purchase, setPurchase] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const {updateTitle} = useAppBar();
  const {config, isLoadingConfig} = useConfig();
  const { t } = useTranslation();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const purchase = await fetchPurchase(id);
        setPurchase(purchase);
        updateTitle('', t('view.purchaseTitle'));
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [t]);

  const [deleteDialog, setDeleteDialog] = useState({
    open: false
  });
  const [deletePaymentDialog, setDeletePaymentDialog] = useState({
    open: false,
    paymentId: null
  });

  const handleDeleteOpen = () => {
    setDeleteDialog({
      open: true
    });
  };

  const handleDeleteClose = () => {
    setDeleteDialog({
      open: false
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}purchases?id=${purchase.id}`,
        { withCredentials: true }
      );
      
      handleDeleteClose();
      navigate(-1);
    } catch (error) {
      handleError(error, snackbar);
    }
  };

  const handleDeletePaymentOpen = (paymentId) => {
    setDeletePaymentDialog({
      open: true,
      paymentId
    });
  };

  const handleDeletePaymentClose = () => {
    setDeletePaymentDialog({
      open: false,
      paymentId: null
    });
  };

  const handleDeletePaymentConfirm = async () => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}purchases_payments?id=${deletePaymentDialog.paymentId}`,
        { withCredentials: true }
      );
      // Refresh the payments list
      const updatedPurchase = await fetchPurchase(id);
      setPurchase(updatedPurchase);
      handleDeletePaymentClose();
    } catch (error) {
      handleError(error, snackbar);
    }
  };

  if (isLoading || isLoadingConfig || !purchase) {
    return <ViewPage isLoading={true} />;
  }

  return (
    <ViewPage 
      isLoading={isLoading || isLoadingConfig}
    >
      <Box sx={{ px: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Typography>{t('view.sellFieldDate')} {new Date(purchase.date).toLocaleString()}</Typography>
          {purchase.receipt && (
            <Typography>{t('view.purchaseFieldReceipt')} {purchase.receipt} {purchase.deleted ? t('view.purchaseDeletedSuffix') : ''}</Typography>
          )}
          {purchase.provider && (
            <Typography>{t('view.purchaseFieldProvider')} {purchase.provider.name}</Typography>
          )}
          <Typography>{t('view.sellFieldUser')} {purchase.user.name}</Typography>
          <Typography>{t('view.sellFieldTotal')} {formatCurrency(purchase.total)}</Typography>
          {purchase.balance > 0 && (
            <Typography>{t('view.purchaseFieldBalance')} {formatCurrency(purchase.balance)}</Typography>
          )}
        </Box>

        <Divider sx={{ marginY: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">{t('common.products')}</Typography>
          <Box>
            {purchase.items?.map((item, index) => (
              <ListItemProductRemove
                key={index}
                product={item.product.name}
                description={getDescription(item)}
                subtotal={formatCurrency(
                  getSubtotal(item.quantity, item.price, item.discount_surcharge)
                )}
                index={index}
                thumbUrl={item.product.thumb_url}
                avatarContent={<InventoryIcon sx={{ fontSize: 20 }} />}
              />
            ))}
          </Box>
        </Box>

        <Divider sx={{ marginY: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">{t('common.payments')}</Typography>
          <List>
            {purchase.payments?.map((payment, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  !payment.deleted && config.permissions?.actions_permissions?.purchases_payments?.delete && (
                    <IconButton
                      edge="end"
                      onClick={() => handleDeletePaymentOpen(payment.id)}
                      sx={{ width: "42px", height: "42px" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )
                }
                sx={{ paddingRight: "56px" }}
              >
                <ListItemText
                  primary={`${new Date(payment.date).toLocaleDateString()}. ${payment.name}: ${formatCurrency(payment.amount)}${payment.deleted ? ' ' + t('view.deleted') : ''}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('view.purchaseDeleteTitle', { id: purchase.id })}</DialogTitle>
        <DialogContent>
          {t('view.purchaseDeleteMessage')}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deletePaymentDialog.open}
        onClose={handleDeletePaymentClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('view.deletePaymentTitle')}</DialogTitle>
        <DialogContent>
          {t('view.deletePaymentMessage')}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeletePaymentClose}>{t('common.cancel')}</Button>
          <Button onClick={handleDeletePaymentConfirm} variant="contained" color="error">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <BottomSheet 
        zIndex={1001}
        buttonGroups={[
          {
            label: t('common.actions'), 
            buttons: [
              ...(config.permissions?.actions_permissions?.purchases?.delete ? [{
                label: t('common.delete'),
                action: handleDeleteOpen,
                icon: <DeleteIcon />
              }] : []),
            ].filter(Boolean)
          }
        ]}
      />
    </ViewPage>
  );
}

export default ViewPurchase; 